import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import {
  acceptTerms, loadMyTerms, loadTermsText, platformDecision, starOneDecision,
  StarOneContext, type TermsStatus, type TermsText,
} from '../lib/terms'
import { useHubLang } from '../lib/i18n'
import { TERMS } from '../lib/hubCopy'
import { messageOf } from '../lib/errors'
import { TermsDocument } from './TermsDocument'
import { viewingAs, viewAsStarOnePreview, rememberViewAsStarOnePreview } from '../lib/viewAsState'

/**
 * 165 (David, 11 Oct): "The terms and conditions page can be on first login."
 *
 * Wraps the whole signed-in tree, INSIDE the language provider (so the terms
 * speak the reader's language) and ABOVE every route (so no screen — not the
 * welcome, not the tour, not a deep link from an email — renders before the
 * terms are agreed). App.tsx is the only caller.
 *
 * ── IT FAILS CLOSED ─────────────────────────────────────────────────────────
 *
 * If my_terms() cannot be read, the hub does not open: the reader gets a retry
 * and a sign-out. The tour fails the other way (158) because a missed tour
 * costs nothing; a missed agreement is the thing this exists to prevent.
 *
 * ── IT ALSO HOLDS STAR ONE'S STATE ──────────────────────────────────────────
 *
 * The same my_terms() read says whether the reader has joined Star One, and
 * StarOneContext hands that to the hub's star cards, the Star One page, the
 * give form and the "+25 stars" pill. `refresh` re-reads it after somebody
 * joins, wherever they joined.
 */
export function TermsGate({ children }: { children: React.ReactNode }) {
  const [rows, setRows] = useState<TermsStatus[] | null>(null)
  const [failed, setFailed] = useState(false)
  // 176. Viewing as somebody who has not agreed: they would see the terms
  // first, so the administrator does too — and may then step past them. The
  // step is presentation only (no server read depends on the platform terms;
  // checked 12 Oct) and nobody can agree FOR them: accept_terms is a write,
  // and every request while viewing as is read-only.
  const [pastForPreview, setPastForPreview] = useState(false)
  const viewing = viewingAs()
  // The same for Star One's own agreement (David, 12 Oct: "on star one he
  // hasn't agreed to the terms so i can't see past that"). Star One's server
  // reads do not depend on it either — only GIVING and ORDERING check it
  // (app.star_one_guard_recognition / _order), and those are writes anyway.
  // Remembered for the View as session, so it holds across pages.
  const [starPast, setStarPast] = useState(() => viewing !== null && viewAsStarOnePreview())

  const refresh = useCallback(async () => {
    try {
      const r = await loadMyTerms()
      setRows(r)
      setFailed(false)
    } catch {
      setFailed(true)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (rows === null) {
    return failed ? <GateProblem onRetry={() => void refresh()} /> : <Splash />
  }

  if (platformDecision(rows) === 'needed' && !(viewing && pastForPreview)) {
    return (
      <PlatformTerms
        onAgreed={refresh}
        viewingAsName={viewing ? viewing.displayName || 'This person' : undefined}
        onPreviewPast={() => setPastForPreview(true)}
      />
    )
  }

  return (
    <StarOneContext.Provider
      value={{
        status: viewing && starPast ? 'agreed' : starOneDecision(rows),
        refresh,
        previewPast: viewing
          ? () => {
              rememberViewAsStarOnePreview()
              setStarPast(true)
            }
          : undefined,
      }}
    >
      {children}
    </StarOneContext.Provider>
  )
}

function Splash() {
  const t = TERMS[useHubLang()]
  return <div className="splash" aria-busy="true">{t.loading}</div>
}

function GateProblem({ onRetry }: { onRetry: () => void }) {
  const t = TERMS[useHubLang()]
  return (
    <main className="signin">
      <div className="signin__card">
        <p className="signin__lede" role="alert">{t.loadFailed}</p>
        <button type="button" className="btn btn--primary" onClick={onRetry}>{t.retry}</button>
        <button type="button" className="btn btn--quiet" onClick={() => supabase.auth.signOut()}>
          {t.signOut}
        </button>
      </div>
    </main>
  )
}

/**
 * The first-sign-in screen. The whole document is on the page — not behind a
 * link — and the button stays off until the box is ticked, so "I agreed" is a
 * thing somebody did rather than a button they pressed to get past.
 */
export function PlatformTerms({
  onAgreed,
  viewingAsName,
  onPreviewPast,
}: {
  onAgreed: () => Promise<void>
  /** 176. Set while an administrator views as this person: no agree, no
   *  sign-out, a way past instead. */
  viewingAsName?: string
  onPreviewPast?: () => void
}) {
  const t = TERMS[useHubLang()]
  const [doc, setDoc] = useState<TermsText | null>(null)
  const [locale, setLocale] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [ticked, setTicked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let live = true
    setLoadFailed(false)
    loadTermsText('platform', locale ?? undefined)
      .then((d) => live && setDoc(d))
      .catch(() => live && setLoadFailed(true))
    return () => {
      live = false
    }
  }, [locale, attempt])

  async function agree() {
    if (!doc || !ticked || busy) return
    setBusy(true)
    setError(null)
    try {
      await acceptTerms(doc.documentId, doc.locale)
      await onAgreed()
    } catch (e) {
      setError(`${t.saveFailed} (${messageOf(e)})`)
      setBusy(false)
    }
  }

  return (
    <main className="signin terms">
      <div className="signin__card terms__card">
        <p className="signin__lede">{t.gateLede}</p>

        {doc ? (
          <div className="terms__scroll" tabIndex={0}>
            <TermsDocument doc={doc} headingLevel={1} onSwitchLocale={setLocale} />
          </div>
        ) : loadFailed ? (
          <>
            <p className="alert" role="alert">{t.loadFailed}</p>
            <button type="button" className="btn btn--quiet" onClick={() => setAttempt((n) => n + 1)}>
              {t.retry}
            </button>
          </>
        ) : (
          <p className="empty" aria-busy="true">{t.loading}</p>
        )}

        {viewingAsName ? (
          <div className="terms__viewas">
            <p className="alert" role="note">
              {viewingAsName} has not agreed to the terms of use yet, so this is the first thing
              they see. You cannot agree for them.
            </p>
            <button type="button" className="btn btn--primary" onClick={onPreviewPast}>
              Continue as if they had agreed
            </button>
          </div>
        ) : doc && (
          <>
            <label className="terms__agree">
              <input
                type="checkbox"
                checked={ticked}
                onChange={(e) => setTicked(e.target.checked)}
                disabled={busy}
              />
              <span>{t.agreePlatform}</span>
            </label>
            {error && <p className="alert" role="alert">{error}</p>}
            <button
              type="button"
              className="btn btn--primary"
              disabled={!ticked || busy}
              onClick={() => void agree()}
            >
              {t.agreeAndContinue}
            </button>
          </>
        )}

        {!viewingAsName && (
          <button type="button" className="btn btn--quiet" onClick={() => supabase.auth.signOut()}>
            {t.signOut}
          </button>
        )}
      </div>
    </main>
  )
}
